// Config → Pipeline & Automation Settings (ethos §10).
// One plainly-sectioned page for every pipeline dial. Each control shows its
// default and a plain-English note. SLA + Rot are live now; Suppression, Caps
// and Throttle are stored now and enforced by Lara later (labelled as such).
import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, Save, Loader2, Clock, Hourglass, ShieldOff, Gauge, Waves, Zap, FileClock } from "lucide-react";

// Schema drives the form so every dial is self-describing and DRY.
const SECTIONS = [
  {
    key: "quote", label: "Quote validity — customer-facing expiry", icon: FileClock, live: true,
    blurb: "How long a quote is marked 'valid until' for the customer. Expiry NEVER locks a quote — an expired quote stays fully editable and can still be accepted if the customer agrees later. Editing a quote refreshes its validity automatically.",
    fields: [
      { k: "validity_hours", label: "Valid for (hours)", type: "number", def: 72, note: "Customer-facing window. New quotes get now + this many hours." },
      { k: "auto_extend_on_save", label: "Auto-extend on every edit", type: "toggle", def: true, note: "Saving a quote refreshes its validity so anything you're working on never reads expired." },
      { k: "never_lock", label: "Never lock expired quotes", type: "toggle", def: true, note: "Expired is informational only — editing and accepting always stay available. Keep this ON." },
      { k: "automap_enabled", label: "Auto-sync status ↔ deal stage", type: "toggle", def: true, note: "Status→stage applies automatically (advance-only); stage→status asks first. Never moves a deal backwards — move those manually." },
    ],
  },
  {
    key: "sla", label: "SLA — warm-reply clock", icon: Clock, live: true,
    blurb: "When a customer replies in AI CX Replied they're at peak warmth. This clock ages that column so no warm reply goes cold. Ships OFF — switch on once Lara is live.",
    fields: [
      { k: "enabled", label: "SLA clock enabled", type: "toggle", def: false, note: "Master switch for the CX Replied aging indicator." },
      { k: "cx_replied_warn_minutes", label: "Warn after (minutes)", type: "number", def: 20, note: "Card turns amber once a reply has waited this long." },
      { k: "cx_replied_alert_minutes", label: "Alert after (minutes)", type: "number", def: 30, note: "Card turns red / flashes past this threshold." },
    ],
  },
  {
    key: "rot", label: "Rot — stale-deal decay", icon: Hourglass, live: true,
    blurb: "A deal with no activity for this many days is flagged rotting on the board. Any of the reset events restarts the clock.",
    fields: [
      { k: "enabled", label: "Rot flagging enabled", type: "toggle", def: true, note: "Show rotting treatment on the board / table." },
      { k: "default_days", label: "Rotting after (days, no activity)", type: "number", def: 3, note: "Global default. Per-stage overrides can be added later." },
      { k: "reset_events", label: "Events that reset the clock", type: "chips", def: ["call_logged", "quote_sent", "stage_change", "note_added"], note: "What counts as activity. Shared by humans and (later) Lara." },
    ],
  },
  {
    key: "suppression", label: "Suppression — never talk over a human", icon: ShieldOff, live: false,
    blurb: "Lara stays silent when a person is already being handled. Stored now, enforced once Lara goes online.",
    fields: [
      { k: "enabled", label: "Suppression enabled", type: "toggle", def: false, note: "Master switch (Lara-side)." },
      { k: "human_touch_hours", label: "Silence after a human touch (hours)", type: "number", def: 24, note: "No automated contact within this window of any human interaction." },
      { k: "open_quote_window_days", label: "Silence while a quote is open (days)", type: "number", def: 7, note: "Hold automation while a live quote is in play." },
    ],
  },
  {
    key: "caps", label: "Caps — frequency & kill-out", icon: Gauge, live: false,
    blurb: "Ceilings so a customer is never over-contacted, and a kill-out so dead deals stop cycling. Stored now, enforced by Lara later.",
    fields: [
      { k: "enabled", label: "Caps enabled", type: "toggle", def: false, note: "Master switch (Lara-side)." },
      { k: "max_contacts", label: "Max automated contacts", type: "number", def: 4, note: "Per period, per customer." },
      { k: "period_days", label: "Per period (days)", type: "number", def: 7, note: "The rolling window the cap applies over." },
      { k: "kill_out_cycles", label: "Kill-out after N dead cycles", type: "number", def: 3, note: "Parks a deal for human review after this many no-response cycles." },
    ],
  },
  {
    key: "throttle", label: "Throttle — ease off a busy column", icon: Waves, live: false,
    blurb: "Stops CX Replied from ballooning by easing automation as the column gets deep or old. Stored now, enforced by Lara later.",
    fields: [
      { k: "enabled", label: "Throttle enabled", type: "toggle", def: false, note: "Master switch (Lara-side)." },
      { k: "cx_replied_depth_limit", label: "Ease off past N deals in CX Replied", type: "number", def: 15, note: "When the column is this deep, slow the intake." },
      { k: "cx_replied_age_limit_minutes", label: "Ease off past age (minutes)", type: "number", def: 45, note: "When items are older than this, slow the intake." },
    ],
  },
];

export default function PipelineAutomation() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/config/pipeline-automation")
      .then(({ data }) => setCfg(data))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const setField = (section, k, v) =>
    setCfg((c) => ({ ...c, [section]: { ...c[section], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(SECTIONS.map((s) => [s.key, cfg[s.key]]));
      const { data } = await api.put("/config/pipeline-automation", body);
      setCfg(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return <Layout><div className="p-10 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-5 pb-16 max-w-4xl" data-testid="pipeline-automation-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-orange-400/80 font-mono">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Config · Pipeline &amp; Automation
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mt-1">Pipeline &amp; Automation Settings</h1>
            <p className="text-xs text-slate-400 mt-1">One page for every pipeline dial. Each control shows its default. Suppression, Caps &amp; Throttle are stored now and enforced by Lara once live.</p>
          </div>
          <button onClick={save} disabled={saving} className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-lg bg-orange-500 text-black font-bold hover:bg-orange-400 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="pa-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>

        <a href="/config/account-manager-sync" data-testid="pa-am-sync-link"
          className="flex items-center gap-3 p-3.5 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] hover:bg-amber-500/[0.1] transition-colors group">
          <SlidersHorizontal className="w-5 h-5 text-amber-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-100">Account Manager Sync →</div>
            <div className="text-[12px] text-slate-400">Fill customers with no account manager from the AM on their quotes. Human-gated: scan → review → apply.</div>
          </div>
        </a>

        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <section key={s.key} className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid={`pa-section-${s.key}`}>
              <div className="flex items-center gap-2.5 mb-1">
                <Icon className="w-4 h-4 text-orange-300" />
                <h3 className="text-sm font-semibold text-white">{s.label}</h3>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${s.live ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`}>
                  {s.live ? "Live now" : "Lara · later"}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mb-4 leading-relaxed">{s.blurb}</p>
              <div className="space-y-3">
                {s.fields.map((f) => {
                  const val = cfg[s.key]?.[f.k];
                  return (
                    <div key={f.k} className="grid sm:grid-cols-[1fr_auto] gap-2 sm:gap-4 items-center border-t border-white/5 pt-3 first:border-0 first:pt-0">
                      <div>
                        <div className="text-[13px] text-slate-200">{f.label}</div>
                        <div className="text-[11px] text-slate-500 leading-snug">{f.note} <span className="text-slate-600">· default {String(Array.isArray(f.def) ? f.def.length + " events" : f.def)}</span></div>
                      </div>
                      <div className="justify-self-start sm:justify-self-end">
                        {f.type === "toggle" && (
                          <button
                            onClick={() => setField(s.key, f.k, !val)}
                            data-testid={`pa-${s.key}-${f.k}`}
                            className={`relative w-11 h-6 rounded-full transition-colors ${val ? "bg-emerald-500" : "bg-slate-600"}`}
                            role="switch" aria-checked={!!val}
                          >
                            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${val ? "left-[22px]" : "left-0.5"}`} />
                          </button>
                        )}
                        {f.type === "number" && (
                          <input
                            type="number" min={0} value={val ?? ""}
                            onChange={(e) => setField(s.key, f.k, Number(e.target.value))}
                            data-testid={`pa-${s.key}-${f.k}`}
                            className="w-24 text-sm px-2.5 py-1.5 rounded-lg border border-white/10 bg-black/40 text-slate-100 text-right focus:outline-none focus:border-orange-400"
                          />
                        )}
                        {f.type === "chips" && (
                          <div className="flex flex-wrap gap-1.5 justify-end" data-testid={`pa-${s.key}-${f.k}`}>
                            {(val || []).map((c) => (
                              <span key={c} className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/10 text-slate-300">{c.replace(/_/g, " ")}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
        <RotStageEditor />
        <p className="text-[11px] text-slate-600 font-mono">Last edited by {cfg.updated_by || "—"}{cfg.updated_at ? ` · ${cfg.updated_at.slice(0, 10)}` : ""}</p>
      </div>
    </Layout>
  );
}

// Per-stage rot overrides — admin tunes how many idle days before a deal/lead in
// each stage is flagged "rotting". 0 (or blank) = that stage never rots.
function RotStageEditor() {
  const [deal, setDeal] = useState([]);
  const [lead, setLead] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/crm/stages"), api.get("/crm/lead-stages")])
      .then(([d, l]) => { setDeal(d.data.items || d.data.stages || []); setLead(l.data.items || l.data.stages || []); })
      .catch(() => toast.error("Failed to load stages"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveRot = async (kind, stage, days) => {
    const key = `${kind}-${stage.key}`;
    setBusy(key);
    try {
      const path = kind === "deal" ? `/crm/stages/${stage.key}` : `/crm/lead-stages/${stage.key}`;
      await api.patch(path, { rot_days: Number(days) });
      toast.success(`${stage.label}: rot ${Number(days) > 0 ? `${days}d` : "never"}`);
      (kind === "deal" ? setDeal : setLead)((arr) => arr.map((s) => s.key === stage.key ? { ...s, rot_days: Number(days) > 0 ? Number(days) : null } : s));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const StageList = ({ kind, stages }) => (
    <div className="space-y-2" data-testid={`rot-stages-${kind}`}>
      {stages.map((s) => (
        <div key={s.key} className="flex items-center gap-3 border-t border-white/5 pt-2 first:border-0 first:pt-0">
          <span className="text-[12px] text-slate-200 flex-1 truncate">{s.label}</span>
          <div className="flex items-center gap-1.5">
            <input
              type="number" min={0} defaultValue={s.rot_days ?? 0}
              data-testid={`rot-input-${kind}-${s.key}`}
              onKeyDown={(e) => { if (e.key === "Enter") saveRot(kind, s, e.target.value); }}
              className="w-20 text-sm px-2 py-1.5 rounded-lg border border-white/10 bg-black/40 text-slate-100 text-right focus:outline-none focus:border-orange-400"
            />
            <span className="text-[10px] text-slate-500 font-mono w-7">days</span>
            <button
              onClick={(e) => saveRot(kind, s, e.currentTarget.parentElement.querySelector("input").value)}
              disabled={busy === `${kind}-${s.key}`}
              data-testid={`rot-save-${kind}-${s.key}`}
              className="text-[10px] font-mono uppercase px-2 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-50">
              {busy === `${kind}-${s.key}` ? "…" : "Set"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="pa-section-rot-stages">
      <div className="flex items-center gap-2.5 mb-1">
        <Hourglass className="w-4 h-4 text-orange-300" />
        <h3 className="text-sm font-semibold text-white">Rot — per-stage overrides</h3>
        <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">Live now</span>
      </div>
      <p className="text-[11px] text-slate-500 mb-4 leading-relaxed">Idle-days before a deal/lead in each stage is flagged rotting. 0 = that stage never rots. Press Enter or Set to save each one.</p>
      {loading ? (
        <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading stages…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-300/80 font-mono mb-2">Deal pipeline</div>
            <StageList kind="deal" stages={deal} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-300/80 font-mono mb-2">Lead pipeline</div>
            <StageList kind="lead" stages={lead} />
          </div>
        </div>
      )}
    </section>
  );
}
